import { isEqual } from 'lodash';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import RouterPropTypes from 'react-router-prop-types';

import { modelOf } from '../../../prop-types';
import OrderStore from '../../../store/OrderStore';
import RecurringOrderStore from '../../../store/RecurringOrderStore';
import RequestState from '../../../types/RequestState';
import { paramsToQueryIdentifier } from '../../../util/query';
import { parse, stringify } from '../../../util/queryString';
import Paginator from '../../common/Paginator';
import ContentForState from '../../loader/ContentForState';
import OrderList from '../OrderList';

@observer
export class MainOrderList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      removeError: null,
    };

    this.loadOrders();
  }

  componentDidMount() {
    this.updateOrderCount();
  }

  componentDidUpdate(prevProps) {
    const { location } = this.props;

    if (!isEqual(prevProps.location.search, location.search)) {
      this.loadOrders();
    }

    if (!isEqual(prevProps.location.pathname, location.pathname)) {
      this.updateOrderCount();
    }
  }

  loadOrders = () => {
    const store = this.getStore();
    if (
      !this.getCurrentResult() &&
      store.orderQueryStates.get(this.getQueryIdentifier()) !==
        RequestState.LOADING
    ) {
      store.loadOrders(this.getQueryParameters()).then(this.updateOrderCount);
    }
  };

  updateOrderCount = () => {
    const { onOrderCount } = this.props;
    if (onOrderCount) {
      onOrderCount(this.getOrderPageItemCount());
    }
  };

  onRemoveClick = (id) => {
    const { recurringOrderStore } = this.props;

    recurringOrderStore
      .removeOrder(id)
      .then(() => {
        recurringOrderStore.loadOrders(this.getQueryParameters());
        this.setState({ removeError: null });
      })
      .catch((error) =>
        this.setState({
          removeError: error,
          requestId: this.state.requestId + 1,
        })
      );
  };

  getStore = () => {
    const { orderStore, recurringOrderStore, useRecurringOrders } = this.props;

    return useRecurringOrders ? recurringOrderStore : orderStore;
  };

  getQueryParameters = () => {
    const {
      location: { search },
      resultsPerPage,
    } = this.props;

    const params = {
      page: 1,
      resultsPerPage,
      ...parse(search),
    };
    params.page = Number(params.page);

    return params;
  };

  getQueryIdentifier = () => {
    return paramsToQueryIdentifier(this.getQueryParameters());
  };

  getCurrentResult = () => {
    const store = this.getStore();

    return store.orderQueryResults.get(this.getQueryIdentifier());
  };

  getOrderPageItems = () => {
    const page = this.getCurrentResult();
    return page ? page.data : [];
  };

  getOrderPageItemCount = () => {
    const page = this.getCurrentResult();
    return page ? page.total : 0;
  };

  getPaginator = () => {
    const { location } = this.props;
    const { last_page: lastPage, current_page: currentPage } =
      this.getCurrentResult();

    if (lastPage < 2) {
      return null;
    }

    let searchTemplate;
    const regex = /page=(\d+?)(?=(&|$))/;
    const pageParameter = `page=:page`;
    if (location.search.charAt(0) !== '?') {
      searchTemplate = location.search + '?' + pageParameter;
    } else {
      searchTemplate = location.search.match(regex)
        ? location.search.replace(regex, pageParameter)
        : location.search + '&' + pageParameter;
    }
    const pathTemplate = location.pathname + searchTemplate;

    return (
      <Paginator
        pathTemplate={pathTemplate}
        currentPage={currentPage}
        lastPage={lastPage}
        pagesToShow={5}
      />
    );
  };

  filterByText = (text) => {
    if (text) {
      const {
        location: { search },
      } = this.props;

      const queryParams = {
        ...parse(search),
      };
      queryParams.text = text;

      this.setQueryParameters(queryParams);
    }

    if (!text && this.getQueryParameters().text) {
      this.clearProductListFilter();
    }
  };

  clearProductListFilter = () => {
    const {
      history,
      location: { search },
    } = this.props;
    const queryParams = parse(search);

    delete queryParams.text;

    history.push({
      search: stringify(queryParams),
    });
  };

  selectSort = (sortOrderKey, ascDesc) => {
    const {
      location: { search },
    } = this.props;

    const queryParams = {
      ...parse(search),
    };

    if (sortOrderKey) {
      queryParams.orderBy = sortOrderKey;
      queryParams.order = ascDesc;
    }

    this.setQueryParameters(queryParams);
  };

  selectSortOrder = (sortOrder) => {
    const {
      location: { search },
    } = this.props;

    const queryParams = {
      ...parse(search),
    };

    if (sortOrder) {
      queryParams.order = sortOrder;
    }

    this.setQueryParameters(queryParams);
  };

  setQueryParameters = (parameters) => {
    this.props.history.push({
      search: stringify(parameters),
    });
  };

  getSelectedSortOrderBy = () => {
    const queryParams = this.getQueryParameters();
    return queryParams.orderBy;
  };

  getSelectedSortOrder = () => {
    const queryParams = this.getQueryParameters();
    return queryParams.order;
  };

  filterByDate = (startDate, endDate) => {
    const {
      location: { search },
    } = this.props;

    const queryParams = {
      ...parse(search),
    };

    if (startDate) {
      queryParams.startDate = startDate;
    }

    if (endDate) {
      queryParams.endDate = endDate;
    }

    if (!startDate && parse(search).startDate) {
      delete queryParams.startDate;
    }

    if (!endDate && parse(search).endDate) {
      delete queryParams.endDate;
    }

    this.setQueryParameters(queryParams);
  };

  render() {
    const {
      singlePage,
      useRecurringOrders,
      location,
      showHeading,
      showFilters,
    } = this.props;
    const store = this.getStore();
    const state = store.orderQueryStates.get(this.getQueryIdentifier());

    return (
      <div className="MainOrderList">
        <ContentForState
          state={state}
          forLoaded={() => (
            <>
              <OrderList
                orders={this.getOrderPageItems()}
                useRecurringOrders={useRecurringOrders}
                onClearProductListFilter={this.clearProductListFilter}
                onFilterByDate={this.filterByDate}
                onFilterByText={this.filterByText}
                onRemoveClick={this.onRemoveClick}
                onSelectSort={this.selectSort}
                onSelectSortOrder={this.selectSortOrder}
                orderCount={this.getOrderPageItemCount()}
                showHeading={showHeading}
                showFilters={showFilters}
                selectedSortOrder={this.getSelectedSortOrder()}
                selectedSortOrderBy={this.getSelectedSortOrderBy()}
                ordersFiltered={Object.keys(parse(location.search)).length > 0}
              />
              {!singlePage &&
                this.getOrderPageItemCount() > 0 &&
                this.getPaginator()}
            </>
          )}
        />
      </div>
    );
  }
}

MainOrderList.propTypes = {
  orderStore: modelOf(OrderStore).isRequired,
  recurringOrderStore: modelOf(RecurringOrderStore).isRequired,
  history: RouterPropTypes.history.isRequired,
  location: RouterPropTypes.location.isRequired,
  onOrderCount: PropTypes.func,
  resultsPerPage: PropTypes.number,
  showFilters: PropTypes.bool,
  showHeading: PropTypes.bool,
  singlePage: PropTypes.bool,
  useRecurringOrders: PropTypes.bool,
};

MainOrderList.defaultProps = {
  showHeading: true,
};

export default withRouter(
  inject('orderStore', 'recurringOrderStore')(MainOrderList)
);
